// src/components/Header/MainNav.tsx
import React, { FC, useState } from "react";
import Logo from "shared/Logo/Logo";
import MenuBar from "shared/MenuBar/MenuBar";
import AvatarDropdown from "./AvatarDropdown";
import Navigation from "shared/Navigation/Navigation";
import CartDropdown from "./CartDropdown";
import { SunIcon, MoonIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import CurrencySwitcher from "components/CurrencySwitcher";

export interface MainNavProps {
  isScrolled: boolean;
}

const MainNav: FC<MainNavProps> = ({ isScrolled }) => {
  const [darkMode, setDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );
  const navigate = useNavigate();

  const toggleDarkMode = () => {
    const isDark = document.documentElement.classList.contains("dark");
    if (!isDark) {
      document.documentElement.classList.add("dark");
      localStorage.theme = "dark";
      setDarkMode(true);
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.theme = "light";
      setDarkMode(false);
    }
  };

  const renderMagnifyingGlassIcon = () => (
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-4.35-4.35M16.65 16.65a7 7 0 1 0-9.9-9.9 7 7 0 0 0 9.9 9.9z"
      />
    </svg>
  );

  const renderContent = () => (
    <div className="h-20 flex w-full items-center justify-between gap-3 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-4">
      <div className="flex min-w-0 items-center gap-3 self-center justify-self-start lg:gap-6">
        <MenuBar />
        <Logo className="hidden shrink-0 md:block" />
      </div>

      <div className="hidden min-w-0 w-full items-center justify-center self-center px-2 lg:flex">
        <Navigation />
      </div>

      <div className="flex min-w-0 items-center justify-self-end self-center gap-1.5 text-slate-700 dark:text-slate-100 lg:gap-2">
        <button
          type="button"
          aria-label="Search products"
          className="hidden h-10 w-10 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:bg-slate-800 sm:h-12 sm:w-12 lg:flex"
          onClick={() => navigate("/page-search")}
        >
          {renderMagnifyingGlassIcon()}
        </button>
        <AvatarDropdown />
        <CartDropdown />

        {/* Sun/Moon Toggle */}
        <button
          type="button"
          onClick={toggleDarkMode}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none"
        >
          {darkMode ? (
            <SunIcon className="w-6 h-6 text-yellow-500" />
          ) : (
            <MoonIcon className="w-6 h-6 text-blue-500" />
          )}
        </button>
        <CurrencySwitcher />
      </div>
    </div>
  );

  return (
    <div
      className={`nc-MainNav relative z-10 transition-colors duration-300
        ${isScrolled ? "border-b border-slate-100 dark:border-slate-700" : "border-b border-transparent"}
      `}
    >
      <div className="container">{renderContent()}</div>
    </div>
  );
};

export default MainNav;